import { Image } from 'components/Image';
import { Text } from 'components/Text';
import { classNames, useState } from 'libs/react';
import { React } from 'libs/react';
import { Money } from 'components/Money';
import { Icon } from 'components/Icon';
import { SaleItemViewer } from 'components/saleItem/SaleItemViewer';
import { useModal } from 'components/modal/ModalStack';
import { calculateBonusVoucherCount, getEffectiveVoucherCount, getMonetaryBonusLabels, getPrimaryQuantityBonusLabel } from 'helpers/bonusHelper';
import './StoreSaleItemCard.scss';
import { Button } from 'components/Button';
import { stockLabel, stockState } from 'helpers/inventory';
import { getPromotionPrice } from 'helpers/promotionPrice';
import { imageSources, STORE_CARD_SIZES } from 'helpers/mediaSources';
import { saleItemHasViewerContent } from '../saleItemViewerContent';
/**
 * @component SaleItemCard
 * @since 1.0.0
 */
export function SaleItemCard(props) {
    var _a, _b;
    const { item, lang, selected, hasDirectSales, selectedOptionCount = 0, selectedAccent = 'green', uncheckedToggleLabel = 'icon' } = props;
    const modal = useModal();
    const [quantity, setQuantity] = useState(1);
    const translation = item.translations.find((entry) => entry.language.code === lang);
    const promoPrice = getPromotionPrice(item.price, item.promotions);
    const showPrice = hasDirectSales && item.configuration === 'FINISHED';
    const stock = stockState(item, quantity, (value) => getEffectiveVoucherCount(value, item.bonuses));
    const stockText = hasDirectSales ? stockLabel(stock) : null;
    const soldOut = hasDirectSales && stock.soldOut;
    const quantityBonusLabel = getPrimaryQuantityBonusLabel(item.bonuses);
    const bonusVoucherCount = calculateBonusVoucherCount(quantity, item.bonuses);
    const effectiveVoucherCount = getEffectiveVoucherCount(quantity, item.bonuses);
    const monetaryBonusLabels = getMonetaryBonusLabels(item.bonuses);
    function applyQuantity(next) {
        var _a;
        const clamped = Math.max(1, Math.min(next, stock.maxQuantity));
        setQuantity(clamped);
        if (selected && props.onQuantityChange) {
            props.onQuantityChange(item.id, clamped);
        }
        else {
            (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props, item.id, clamped);
        }
    }
    const onSaleItemCardClick = () => {
        var _a;
        if (soldOut && !selected)
            return;
        (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props, item.id, quantity);
    };
    function onToggleChange(event) {
        var _a;
        event === null || event === void 0 ? void 0 : event.stopPropagation();
        if (soldOut && !selected)
            return;
        (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props, item.id, quantity);
    }
    function onSaleItemDetailClick(event) {
        var _a;
        event.stopPropagation();
        if (item) {
            modal.present({
                title: (_a = translation === null || translation === void 0 ? void 0 : translation.title) !== null && _a !== void 0 ? _a : item.sku,
                picto: '',
                className: 'deal-sale-item-selector-item-modal',
                modal: React.createElement(SaleItemViewer, { item: item, lang: lang, isStore: true })
            });
        }
    }
    const className = classNames('sale-item-card', {
        'sale-item-card--selected': selected,
        'sale-item-card--selected-primary': selected && selectedAccent === 'primary',
        'sale-item-card--has-options': selected && selectedOptionCount > 0,
        'sale-item-card--sold-out': soldOut && !selected
    });
    const image = (_a = item.image) === null || _a === void 0 ? void 0 : _a.image;
    const title = (_b = translation === null || translation === void 0 ? void 0 : translation.title) !== null && _b !== void 0 ? _b : item.sku;
    const showInfo = saleItemHasViewerContent(item, lang);
    return (React.createElement("div", { className: className, onClick: onSaleItemCardClick },
        React.createElement("div", { className: "sale-item-card__head" }, (image === null || image === void 0 ? void 0 : image.url) ? (React.createElement(Image, Object.assign({ className: "sale-item-card__image", lazy: true }, imageSources(image, STORE_CARD_SIZES)))) : (React.createElement("div", { className: "sale-item-card__placeholder", "aria-hidden": "true" },
            React.createElement(Icon, { value: "image" })))),
        React.createElement("div", { className: "sale-item-card__body" },
            React.createElement("div", { className: "sale-item-card__body-header" },
                React.createElement(Text, { value: translation === null || translation === void 0 ? void 0 : translation.title, className: "sale-item-card__title" }),
                showInfo && (React.createElement("button", { type: "button", className: "sale-item-card__info-btn", "aria-label": t('More information'), onClick: onSaleItemDetailClick },
                    React.createElement(Icon, { value: "circle-info", className: "sale-item-card__info" })))),
            (translation === null || translation === void 0 ? void 0 : translation.summary) && (React.createElement(Text, { html: true, value: translation.summary, className: "sale-item-card__summary" })),
            selected && selectedOptionCount > 0 && (React.createElement(Text, { value: `${selectedOptionCount} ${t('option(s) selected')}`, className: "sale-item-card__options-count" })),
            stockText && (React.createElement(Text, { value: stockText, className: classNames('sale-item-card__stock', {
                    'sale-item-card__stock--sold-out': stock.soldOut
                }) })),
            quantityBonusLabel && (React.createElement(Text, { value: quantityBonusLabel, className: "sale-item-card__bonus-label" })),
            bonusVoucherCount > 0 && (React.createElement(Text, { value: `${t('You will receive')} ${effectiveVoucherCount} ${t('vouchers')} (${bonusVoucherCount} ${t('free')})`, className: "sale-item-card__bonus-count" })),
            monetaryBonusLabels.map((label) => (React.createElement(Text, { key: label, value: label, className: "sale-item-card__bonus-label" })))),
        React.createElement("div", { className: "sale-item-card__footer", onClick: (event) => event.stopPropagation() },
            showPrice &&
                (promoPrice !== null ? (React.createElement("div", { className: "sale-item-card__price-promo" },
                    React.createElement(Money, { value: item.price, className: "sale-item-card__price--original" }),
                    React.createElement(Money, { value: promoPrice, className: "sale-item-card__price--promo" }))) : (React.createElement(Money, { value: item.price, className: "sale-item-card__price" }))),
            React.createElement("div", { className: "sale-item-card__controls" },
                showPrice && (React.createElement("div", { className: "sale-item-card__stepper" },
                    React.createElement("button", { type: "button", className: "sale-item-card__stepper-btn", "aria-label": `${t('Decrease quantity')} – ${title}`, disabled: quantity <= 1, onClick: () => applyQuantity(quantity - 1) },
                        React.createElement(Icon, { value: "minus" })),
                    React.createElement("span", { className: "sale-item-card__stepper-value", "aria-live": "polite" }, quantity),
                    React.createElement("button", { type: "button", className: "sale-item-card__stepper-btn", "aria-label": `${t('Increase quantity')} – ${title}`, disabled: quantity >= stock.maxQuantity, onClick: () => applyQuantity(quantity + 1) },
                        React.createElement(Icon, { value: "plus" })))),
                React.createElement(Button, { picto: selected ? 'times' : 'plus', label: selected ? t('Remove') : t('Add'), ariaLabel: `${selected ? t('Remove') : t('Add')} – ${title}`, ariaPressed: selected, className: classNames('sale-item-card__toggle', {
                        'sale-item-card__toggle--selected': selected
                    }), selected: selected, disabled: soldOut && !selected, onPress: onToggleChange })))));
}
